import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faLinkedin,
  faInstagram,
  faTwitter,
  faGithub,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faClock,
  faUser,
  faComments,
  faPaperPlane,
  faCheckCircle,
  faSpinner,
  faCalendarAlt,
  faGlobe,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../ApiService";

import SectionContainer from "../../components/SectionContainer";
import Reveal from "../../components/Reveal";

const ContactUsSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    inquiryType: "general",
    message: "",
    recipient: "support@geniusnexa.com",
  });

  const [statusMessage, setStatusMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState("");

  // Anti-bot math challenge
  const [challenge, setChallenge] = useState({ num1: 0, num2: 0, answer: "" });

  const contactInfo = [
    {
      icon: faEnvelope,
      title: "Email Us",
      details: "support@geniusnexa.com",
      description: "Send us an email anytime",
      color: "from-blue-500 to-indigo-600",
      action: "mailto:support@geniusnexa.com",
    },
    {
      icon: faPhone,
      title: "Call Us",
      details: "+91 9876543210",
      description: "Mon-Fri from 8am to 6pm",
      color: "from-green-500 to-emerald-600",
      action: "tel:+919876543210",
    },
    {
      icon: faMapMarkerAlt,
      title: "Visit Us",
      details: "Tamil Nadu, India",
      description: "24 C, Extension 1 Udyar Avenue",
      color: "from-[#E5C558] to-[#d4b54d]",
      action: "#location",
    },
    {
      icon: faClock,
      title: "Working Hours",
      details: "Mon - Sat: 9AM - 6PM",
      description: "Flexible for urgent needs",
      color: "from-purple-500 to-violet-600",
      action: null,
    },
  ];

  const inquiryTypes = [
    { value: "general", label: "General Inquiry", icon: faQuestionCircle },
    { value: "project", label: "New Project", icon: faPaperPlane },
    { value: "support", label: "Technical Support", icon: faComments },
    { value: "partnership", label: "Partnership", icon: faGlobe },
    { value: "consultation", label: "Free Consultation", icon: faCalendarAlt },
  ];

  const socialLinks = [
    {
      icon: faLinkedin,
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/103303143/admin/feed/posts/",
      color: "text-blue-600 hover:text-blue-800",
    },
    {
      icon: faFacebook,
      name: "Facebook",
      url: "https://www.facebook.com/yourprofile",
      color: "text-blue-700 hover:text-blue-900",
    },
    {
      icon: faInstagram,
      name: "Instagram",
      url: "https://www.instagram.com/yourprofile",
      color: "text-pink-600 hover:text-pink-800",
    },
    {
      icon: faTwitter,
      name: "Twitter",
      url: "https://twitter.com/yourprofile",
      color: "text-blue-400 hover:text-blue-600",
    },
    {
      icon: faGithub,
      name: "GitHub",
      url: "https://github.com/yourprofile",
      color:
        "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
    },
  ];

  const generateChallenge = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setChallenge({ num1, num2, answer: "" });
  };

  useEffect(() => {
    generateChallenge();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage("");

    // Human validation
    if (parseInt(challenge.answer, 10) !== challenge.num1 + challenge.num2) {
      setStatusMessage("⚠️ Please solve the math question correctly.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await apiService.sendEmail({ ...formData });

      if (result?.status === "success") {
        setStatusMessage(
          `✅ Thank you, ${formData.name}. We’ll get back to you soon.`
        );
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          inquiryType: "general",
          message: "",
          recipient: "support@geniusnexa.com",
        });
        generateChallenge();
      } else {
        setStatusMessage("❌ Error sending message. Please try again.");
      }
    } catch (error) {
      console.error("Email send error:", error);
      setStatusMessage("❌ Something went wrong. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SectionContainer id="contact-us" variant="gradient">
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="contact-pattern"
                width="30"
                height="30"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="15" cy="15" r="2" fill="currentColor" />
                <circle cx="5" cy="5" r="1" fill="currentColor" opacity="0.5" />
                <circle
                  cx="25"
                  cy="25"
                  r="1"
                  fill="currentColor"
                  opacity="0.5"
                />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#contact-pattern)" />
          </svg>
        </div>

        {/* Header Section */}
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/10 rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/20">
            <FontAwesomeIcon icon={faComments} className="mr-2" />
            Let's Connect
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
            Get In{" "}
            <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
              Touch
            </span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Ready to transform your business? Reach out and our expert team will
            respond promptly with tailored solutions.
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20 relative z-10">
          {contactInfo.map((info, index) => (
            <Reveal key={index} delay={0.1 + index * 0.1}>
              <div className="group bg-white dark:bg-gray-800 rounded-2xl p-6 text-center shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col">
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                >
                  <FontAwesomeIcon
                    icon={info.icon}
                    className="text-white text-2xl"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {info.title}
                </h3>
                <div className="text-[#E5C558] font-semibold mb-2">
                  {info.details}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex-grow">
                  {info.description}
                </p>
                {info.action && (
                  <a
                    href={info.action}
                    className="inline-block mt-4 px-4 py-2 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  >
                    {info.action.startsWith("mailto:")
                      ? "Send Email"
                      : info.action.startsWith("tel:")
                      ? "Call Now"
                      : "View Map"}
                  </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {/* Main Content Section */}
        <div className="flex flex-col lg:flex-row items-stretch gap-16 relative z-10">
          {/* Enhanced Contact Form */}
          <Reveal className="w-full lg:w-2/3 flex">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 relative overflow-hidden w-full flex flex-col">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5 overflow-hidden">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <defs>
                    <pattern
                      id="form-pattern"
                      width="40"
                      height="40"
                      patternUnits="userSpaceOnUse"
                    >
                      <rect
                        x="18"
                        y="18"
                        width="4"
                        height="4"
                        fill="currentColor"
                        opacity="0.3"
                      />
                      <rect
                        x="8"
                        y="8"
                        width="2"
                        height="2"
                        fill="currentColor"
                        opacity="0.5"
                      />
                      <rect
                        x="30"
                        y="30"
                        width="2"
                        height="2"
                        fill="currentColor"
                        opacity="0.5"
                      />
                    </pattern>
                  </defs>
                  <rect width="100" height="100" fill="url(#form-pattern)" />
                </svg>
              </div>
              {/* Form Header */}
              <div className="text-center mb-8 relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon
                    icon={faPaperPlane}
                    className="text-white text-2xl"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Send us a Message
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Fill out the form below and we'll get back to you within 24
                  hours
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                {/* Name and Email Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="mr-2 text-[#E5C558]"
                      />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => setFocusedField("")}
                      required
                      className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all duration-300 ${
                        focusedField === "name"
                          ? "border-[#E5C558] shadow-lg"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="mr-2 text-[#E5C558]"
                      />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField("")}
                      required
                      className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all duration-300 ${
                        focusedField === "email"
                          ? "border-[#E5C558] shadow-lg"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Phone and Company Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="mr-2 text-[#E5C558]"
                      />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("phone")}
                      onBlur={() => setFocusedField("")}
                      className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all duration-300 ${
                        focusedField === "phone"
                          ? "border-[#E5C558] shadow-lg"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <FontAwesomeIcon
                        icon={faGlobe}
                        className="mr-2 text-[#E5C558]"
                      />
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("company")}
                      onBlur={() => setFocusedField("")}
                      className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all duration-300 ${
                        focusedField === "company"
                          ? "border-[#E5C558] shadow-lg"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                      placeholder="Your company name"
                    />
                  </div>
                </div>

                {/* Inquiry Type */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="mr-2 text-[#E5C558]"
                    />
                    Inquiry Type
                  </label>
                  <select
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all duration-300"
                  >
                    {inquiryTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon
                      icon={faComments}
                      className="mr-2 text-[#E5C558]"
                    />
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField("")}
                    rows="5"
                    required
                    className={`w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all duration-300 resize-none ${
                      focusedField === "message"
                        ? "border-[#E5C558] shadow-lg"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    placeholder="Tell us about your project or inquiry..."
                  ></textarea>
                </div>

                {/* Human Verification */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                  <label className="block text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    Security Check: What is{" "}
                    <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {challenge.num1} + {challenge.num2}
                    </span>
                    ?
                  </label>
                  <input
                    type="text"
                    value={challenge.answer}
                    onChange={(e) =>
                      setChallenge((prev) => ({
                        ...prev,
                        answer: e.target.value,
                      }))
                    }
                    required
                    placeholder="Enter your answer"
                    className="w-full px-4 py-3 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-blue-900/30 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 rounded-xl text-white font-bold transition-all duration-300 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:shadow-xl hover:shadow-[#E5C558]/30 hover:scale-[1.02] flex items-center justify-center space-x-2 ${
                    isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin"
                      />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      <span>Send Message</span>
                    </>
                  )}
                </button>

                {statusMessage && (
                  <div
                    className={`p-4 rounded-xl text-center font-medium ${
                      statusMessage.includes("✅")
                        ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
                        : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
                    }`}
                  >
                    {statusMessage}
                  </div>
                )}
              </form>
            </div>
          </Reveal>

          {/* Social and Location Sidebar */}
          <Reveal className="w-full lg:w-1/3 flex">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 w-full flex flex-col relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5 overflow-hidden">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <defs>
                    <pattern
                      id="sidebar-pattern"
                      width="30"
                      height="30"
                      patternUnits="userSpaceOnUse"
                    >
                      <circle cx="15" cy="15" r="2" fill="currentColor" />
                      <circle
                        cx="5"
                        cy="5"
                        r="1"
                        fill="currentColor"
                        opacity="0.5"
                      />
                      <circle
                        cx="25"
                        cy="25"
                        r="1"
                        fill="currentColor"
                        opacity="0.5"
                      />
                    </pattern>
                  </defs>
                  <rect width="100" height="100" fill="url(#sidebar-pattern)" />
                </svg>
              </div>
              <div className="space-y-8 h-full flex flex-col relative z-10">
                {/* Social Media Section */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                  <h3 className="text-xl font-bold text-center mb-6 text-gray-900 dark:text-white">
                    <FontAwesomeIcon
                      icon={faGlobe}
                      className="mr-2 text-[#E5C558]"
                    />
                    Follow Us
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group flex flex-col items-center p-3 rounded-xl bg-gray-50 dark:bg-gray-700 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${social.color}`}
                        title={social.name}
                      >
                        <FontAwesomeIcon
                          icon={social.icon}
                          size="xl"
                          className="mb-2 group-hover:scale-110 transition-transform duration-300"
                        />
                        <span className="text-xs font-medium">
                          {social.name}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Location Section */}
                <div
                  id="location"
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-xl font-bold text-center mb-6 text-gray-900 dark:text-white">
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="mr-2 text-[#E5C558]"
                    />
                    Our Location
                  </h3>
                  <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
                    <iframe
                      src="https://www.google.com/maps?q=11.1394965,78.5864118&hl=en&z=17&output=embed"
                      width="100%"
                      height="250"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="GeniusNexa Office Location"
                    ></iframe>
                  </div>
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <div className="text-center">
                      <p className="font-bold text-gray-900 dark:text-white mb-2">
                        GeniusNexa
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        24 C, Extension 1 Udyar Avenue
                        <br />
                        Thuraiyur, Tiruchirappalli district
                        <br />
                        Tamil Nadu, India
                        <br />
                        <span className="font-medium">Pin Code: 621010</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Response Promise */}
                <div className="bg-gradient-to-br from-[#E5C558]/10 to-[#d4b54d]/10 rounded-2xl p-6 border border-[#E5C558]/20 flex-grow flex items-center">
                  <div className="text-center w-full">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-full flex items-center justify-center mx-auto mb-4">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-white text-lg"
                      />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      Quick Response
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      We respond to all inquiries within 24 hours. For urgent
                      matters, call us directly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </SectionContainer>
  );
};

export default ContactUsSection;
